import { useState } from 'react';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';
import { userStore } from '@/stores/user.store';
import { handleMailTo } from '@/utils/platform';

import { teamApis, useTeamSsoQuery } from '../team.query';

const STATUS_LABELS = {
  not_set_up: 'Not set up',
  in_progress: 'Setup in progress. Finish it in the browser, then come back.',
  connected: 'Connected',
};

export function TeamSsoCard({
  teamId,
  teamName,
}: {
  teamId: string;
  teamName: string;
}) {
  const { data: sso } = useTeamSsoQuery(teamId);
  const [isOpening, setIsOpening] = useState(false);
  const isOff = sso?.enabled === false;
  const isConnected = sso?.enabled && sso.status === 'connected';

  const contactSupport = () =>
    handleMailTo(
      'support@littlebird.ai',
      `Enable SSO for ${teamName}`,
      `Please enable single sign-on for our team.\n\nTeam: ${teamName}\nTeam ID: ${teamId}\nRequested by: ${userStore.user?.email}`
    );

  const openSetup = async () => {
    setIsOpening(true);
    await teamApis.openSsoSetup(teamId);
    setIsOpening(false);
  };

  const setRequired = async (required: boolean) => {
    const confirmed =
      !required ||
      (await ModalsManager.showResolvable(ConfirmDialog, {
        title: `Require SSO for ${sso?.domains.join(', ')}?`,
        description:
          'Everyone else on these domains must use SSO the next time they open the desktop or web app, or sign in on mobile.',
        confirmText: 'Require SSO',
        confirmVariant: 'default',
      }));
    if (confirmed) await teamApis.setSsoRequired(teamId, required);
  };

  return (
    <YStack className="w-full gap-3">
      <SettingsSection.GroupHeader>Single Sign-On</SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <SettingsSection.Item direction="horizontal">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>SSO</SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              {isOff
                ? 'Single sign-on is available for your team on request.'
                : sso && STATUS_LABELS[sso.status]}
              {isConnected && ` (${sso.domains.join(', ')})`}
            </SettingsSection.ItemDescription>
            {isConnected && (
              <SettingsSection.ItemDescription>
                To make someone a team admin from your identity provider, add
                them to a group named littlebird-admins and sync that group to
                Littlebird.
              </SettingsSection.ItemDescription>
            )}
          </SettingsSection.ItemHeader>
          {isOff ? (
            <Button
              variant="secondary"
              onClick={contactSupport}
              className="w-36"
            >
              Contact support
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={openSetup}
              isLoading={isOpening}
              disabled={!sso}
              className="w-36"
            >
              {sso?.status === 'not_set_up' ? 'Set up SSO' : 'Manage'}
            </Button>
          )}
        </SettingsSection.Item>
        {!isOff && (
          <SettingsSection.Item direction="horizontal">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>Require SSO</SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Everyone with an email on these domains must sign in through
                SSO, including people outside this team. It applies the next
                time they open the desktop or web app, or sign in on mobile.
                Team owners and admins can still sign in another way.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <SettingsSection.Switch
              checked={sso?.required ?? false}
              onCheckedChange={setRequired}
              disabled={!isConnected}
            />
          </SettingsSection.Item>
        )}
      </SettingsSection.Group>
    </YStack>
  );
}
